import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Barcode, Loader2, CheckCircle2, XCircle, Package, Store, Smartphone, Monitor, Truck, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUniqueValidation } from "@/hooks/useUniqueValidation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ProductImageUpload } from "./ProductImageUpload";
import { useProductionLocations } from "@/hooks/useProductionLocations";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;
type Category = Tables<"categories">;

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100, "Máximo 100 caracteres"),
  description: z.string().max(500, "Máximo 500 caracteres").optional(),
  price: z.coerce.number().min(0, "Preço deve ser positivo"),
  category_id: z.string().optional(),
  production_location_id: z.string().optional(),
  barcode: z.string().max(50, "Máximo 50 caracteres").optional(),
  is_active: z.boolean(),
  tracks_stock: z.boolean(),
  current_stock: z.coerce.number().min(0, "Estoque deve ser positivo").optional(),
  minimum_stock: z.coerce.number().min(0, "Estoque mínimo deve ser positivo").optional(),
  cost_per_unit: z.coerce.number().min(0, "Custo deve ser positivo").optional(),
  available_pdv: z.boolean(),
  available_table: z.boolean(),
  available_tablet: z.boolean(),
  available_delivery: z.boolean(),
  is_featured: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: Product | null;
  categories: Category[];
  onSubmit: (data: FormData, imageFile?: File) => Promise<void>;
  isSubmitting?: boolean;
}

export function ProductFormDialog({
  open,
  onOpenChange,
  product,
  categories,
  onSubmit,
  isSubmitting,
}: ProductFormDialogProps) {
  const isEditing = !!product;
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const { locations, getActiveLocations } = useProductionLocations();
  const activeLocations = getActiveLocations();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      category_id: undefined,
      production_location_id: undefined,
      barcode: "",
      is_active: true,
      tracks_stock: false,
      current_stock: 0,
      minimum_stock: 0,
      cost_per_unit: 0,
      available_pdv: true,
      available_table: true,
      available_tablet: true,
      available_delivery: true,
      is_featured: false,
    },
  });

  const watchTracksStock = form.watch("tracks_stock");

  const { isChecking, isUnique, checkUnique, reset: resetValidation } = useUniqueValidation({
    table: "products",
    column: "barcode",
    excludeId: product?.id,
  });

  const watchedBarcode = form.watch("barcode");

  useEffect(() => {
    if (product) {
      form.reset({
        name: product.name,
        description: product.description || "",
        price: Number(product.price),
        category_id: product.category_id || undefined,
        production_location_id: (product as any).production_location_id || undefined,
        barcode: (product as any).barcode || "",
        is_active: product.is_active ?? true,
        tracks_stock: (product as any).tracks_stock ?? false,
        current_stock: Number((product as any).current_stock) || 0,
        minimum_stock: Number((product as any).minimum_stock) || 0,
        cost_per_unit: Number((product as any).cost_per_unit) || 0,
        available_pdv: (product as any).available_pdv ?? true,
        available_table: (product as any).available_table ?? true,
        available_tablet: (product as any).available_tablet ?? true,
        available_delivery: (product as any).available_delivery ?? true,
        is_featured: (product as any).is_featured ?? false,
      });
      resetValidation();
    } else {
      form.reset({
        name: "",
        description: "",
        price: 0,
        category_id: undefined,
        production_location_id: undefined,
        barcode: "",
        is_active: true,
        tracks_stock: false,
        current_stock: 0,
        minimum_stock: 0,
        cost_per_unit: 0,
        available_pdv: true,
        available_table: true,
        available_tablet: true,
        available_delivery: true,
        is_featured: false,
      });
      resetValidation();
    }
    setImageFile(null);
    setRemoveImage(false);
  }, [product, form, open, resetValidation]);

  // Check barcode uniqueness when it changes
  useEffect(() => {
    const currentBarcode = watchedBarcode?.trim() || "";
    const originalBarcode = (product as any)?.barcode || "";
    
    // Only check if barcode has a value and is different from original
    if (currentBarcode && (!isEditing || currentBarcode !== originalBarcode)) {
      checkUnique(currentBarcode);
    } else {
      resetValidation();
    }
  }, [watchedBarcode, isEditing, product, checkUnique, resetValidation]);

  // Determine if barcode validation should be shown and if form can submit
  const showBarcodeValidation = !!(watchedBarcode?.trim()) && (!isEditing || watchedBarcode?.trim() !== ((product as any)?.barcode || ""));
  const isBarcodeValid = !showBarcodeValidation || isUnique === true;
  const canSubmit = !isSubmitting && !isChecking && isBarcodeValid;

  const handleSubmit = async (data: FormData) => {
    if (isUnique === false) return; // Don't submit if duplicate barcode
    await onSubmit(
      {
        ...data,
        category_id: data.category_id === "none" ? null : data.category_id,
        production_location_id: data.production_location_id === "none" ? null : data.production_location_id,
      },
      imageFile || undefined
    );
    form.reset();
    setImageFile(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Produto" : "Novo Produto"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <ProductImageUpload
              currentImageUrl={removeImage ? null : product?.image_url}
              onImageSelect={setImageFile}
              onRemoveImage={() => setRemoveImage(true)}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: X-Burger" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição (opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva o produto..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preço</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        {...field}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(field.value || 0)}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || "none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Sem categoria</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="production_location_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Local de Produção (Impressão)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || "none"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione onde imprimir..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">Não imprimir</SelectItem>
                      {activeLocations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.printer_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Define onde o pedido será impresso automaticamente
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="barcode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Barcode className="h-4 w-4" />
                    Código de Barras (opcional)
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="Ex: 7891234567890"
                        {...field}
                        data-barcode-scanner
                        className={cn(
                          showBarcodeValidation && isUnique === false && "border-destructive focus-visible:ring-destructive",
                          showBarcodeValidation && isUnique === true && "border-green-500 focus-visible:ring-green-500"
                        )}
                      />
                      {showBarcodeValidation && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {isChecking ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : isUnique === true ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : isUnique === false ? (
                            <XCircle className="h-4 w-4 text-destructive" />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  {showBarcodeValidation && isUnique === false ? (
                    <p className="text-sm text-destructive">
                      Já existe um produto com este código de barras
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Use um leitor de código de barras ou digite manualmente
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tracks_stock"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                  <div className="space-y-0.5">
                    <FormLabel className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Controla Estoque
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Ative para produtos de revenda (bebidas, etc.)
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {watchTracksStock && (
              <div className="rounded-lg border p-4 space-y-4 bg-muted/20">
                <p className="text-sm font-medium text-muted-foreground">
                  Configurações de Estoque
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <FormField
                    control={form.control}
                    name="current_stock"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estoque Atual</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="minimum_stock"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estoque Mínimo</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cost_per_unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Custo Unit.</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0,00"
                            {...field}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(field.value || 0)}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Sales Channels */}
            <div className="rounded-lg border p-4 space-y-3">
              <p className="text-sm font-medium">Canais de Venda</p>
              <p className="text-xs text-muted-foreground">
                Defina onde este produto estará disponível
              </p>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="available_pdv"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        PDV
                      </FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="available_table"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
                        <Store className="h-4 w-4 text-muted-foreground" />
                        Mesa (QR)
                      </FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="available_tablet"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                        Tablet
                      </FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="available_delivery"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        Delivery
                      </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="is_featured"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                  <div className="space-y-0.5">
                    <FormLabel className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      Destacar no Tablet
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Aparece na seção "Destaques" do autoatendimento
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Ativo</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Produtos inativos não aparecem no cardápio
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {isSubmitting ? "Salvando..." : isEditing ? "Salvar" : "Criar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
